import { Node, Edge } from 'reactflow';
import { caffeSempioneTemplates } from './caffeSempioneTemplates';

export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  nodes: Node[];
  edges: Edge[];
  tags: string[];
}

export const flowTemplates: FlowTemplate[] = [
  // Caffè Sempione Templates - In primo piano
  ...caffeSempioneTemplates,
  
  // Customer Support Templates
  {
    id: 'customer-support-basic',
    name: 'Supporto Cliente Base',
    description: 'Risposta automatica con FAQ e inoltro ad operatore',
    category: 'Supporto Cliente',
    icon: '🎧',
    tags: ['supporto', 'faq', 'operatore'],
    nodes: [
      {
        id: '1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Messaggio Ricevuto',
          triggerType: 'message_received',
          config: {}
        }
      },
      {
        id: '2',
        type: 'action',
        position: { x: 250, y: 150 },
        data: {
          label: 'Messaggio di Benvenuto',
          actionType: 'send_message',
          config: {
            message: 'Ciao! 👋 Sono l\'assistente virtuale. Come posso aiutarti oggi?\n\n1️⃣ Informazioni prodotti\n2️⃣ Stato ordine\n3️⃣ Assistenza tecnica\n4️⃣ Parla con un operatore'
          }
        }
      },
      {
        id: '3',
        type: 'condition',
        position: { x: 250, y: 250 },
        data: {
          label: 'Verifica Risposta',
          conditionType: 'contains',
          config: {
            value: '1'
          }
        }
      },
      {
        id: '4',
        type: 'action',
        position: { x: 100, y: 350 },
        data: {
          label: 'Info Prodotti',
          actionType: 'send_message',
          config: {
            message: 'Ecco i nostri prodotti principali...'
          }
        }
      },
      {
        id: '5',
        type: 'action',
        position: { x: 400, y: 350 },
        data: {
          label: 'Inoltra a Operatore',
          actionType: 'send_message',
          config: {
            message: 'Ti sto mettendo in contatto con un operatore. Attendi qualche istante...'
          }
        }
      }
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3', animated: true },
      { id: 'e3-4', source: '3', target: '4', animated: true, sourceHandle: 'true' },
      { id: 'e3-5', source: '3', target: '5', animated: true, sourceHandle: 'false' }
    ]
  },

  // E-commerce Templates
  {
    id: 'order-confirmation',
    name: 'Conferma Ordine',
    description: 'Flusso automatico per confermare ordini e inviare tracking',
    category: 'E-commerce',
    icon: '🛒',
    tags: ['ordine', 'conferma', 'tracking'],
    nodes: [
      {
        id: '1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Webhook Ordine',
          triggerType: 'webhook',
          config: {
            webhookUrl: '/api/orders/new'
          }
        }
      },
      {
        id: '2',
        type: 'template',
        position: { x: 250, y: 150 },
        data: {
          label: 'Template Conferma',
          templateType: 'order_confirmation',
          config: {
            language: 'it'
          }
        }
      },
      {
        id: '3',
        type: 'delay',
        position: { x: 250, y: 250 },
        data: {
          label: 'Attendi 1 ora',
          delayType: 'fixed',
          config: {
            delayTime: 1,
            delayUnit: 'hours'
          }
        }
      },
      {
        id: '4',
        type: 'action',
        position: { x: 250, y: 350 },
        data: {
          label: 'Invia Tracking',
          actionType: 'send_message',
          config: {
            message: '📦 Il tuo ordine è in preparazione! Ecco il link per il tracking: {{tracking_url}}'
          }
        }
      },
      {
        id: '5',
        type: 'analytics',
        position: { x: 250, y: 450 },
        data: {
          label: 'Traccia Evento',
          analyticsType: 'track_event',
          config: {
            eventName: 'order_confirmation_sent'
          }
        }
      }
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3', animated: true },
      { id: 'e3-4', source: '3', target: '4', animated: true },
      { id: 'e4-5', source: '4', target: '5', animated: true }
    ]
  },

  // Marketing Templates
  {
    id: 'newsletter-broadcast',
    name: 'Newsletter Broadcast',
    description: 'Invio newsletter personalizzata con segmentazione',
    category: 'Marketing',
    icon: '📨',
    tags: ['newsletter', 'broadcast', 'marketing'],
    nodes: [
      {
        id: '1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Programmazione',
          triggerType: 'schedule',
          config: {
            schedule: '0 9 * * MON'
          }
        }
      },
      {
        id: '2',
        type: 'filter',
        position: { x: 250, y: 150 },
        data: {
          label: 'Filtra Utenti Attivi',
          filterType: 'user_type',
          config: {
            userType: 'existing'
          }
        }
      },
      {
        id: '3',
        type: 'broadcast',
        position: { x: 250, y: 250 },
        data: {
          label: 'Broadcast Newsletter',
          broadcastType: 'segment',
          config: {
            dynamicContent: true
          }
        }
      },
      {
        id: '4',
        type: 'template',
        position: { x: 250, y: 350 },
        data: {
          label: 'Template Newsletter',
          templateType: 'newsletter',
          config: {
            language: 'it'
          }
        }
      },
      {
        id: '5',
        type: 'analytics',
        position: { x: 250, y: 450 },
        data: {
          label: 'Report Invio',
          analyticsType: 'report_generator',
          config: {
            reportType: 'broadcast_performance'
          }
        }
      }
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3', animated: true },
      { id: 'e3-4', source: '3', target: '4', animated: true },
      { id: 'e4-5', source: '4', target: '5', animated: true }
    ]
  },

  // Lead Generation
  {
    id: 'lead-qualification',
    name: 'Qualificazione Lead',
    description: 'Chatbot per qualificare lead con domande mirate',
    category: 'Lead Generation',
    icon: '🎯',
    tags: ['lead', 'chatbot', 'qualificazione'],
    nodes: [
      {
        id: '1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Primo Contatto',
          triggerType: 'message_received',
          config: {}
        }
      },
      {
        id: '2',
        type: 'chatbot',
        position: { x: 250, y: 150 },
        data: {
          label: 'Bot Qualificazione',
          chatbotType: 'conversation_flow',
          config: {
            conversationSteps: [
              'Nome e azienda',
              'Ruolo',
              'Budget',
              'Timeline'
            ]
          }
        }
      },
      {
        id: '3',
        type: 'condition',
        position: { x: 250, y: 250 },
        data: {
          label: 'Lead Qualificato?',
          conditionType: 'user_attribute',
          config: {
            attribute: 'lead_score',
            operator: '>',
            value: '70'
          }
        }
      },
      {
        id: '4',
        type: 'integration',
        position: { x: 100, y: 350 },
        data: {
          label: 'Salva in CRM',
          integrationType: 'crm',
          config: {
            crmType: 'salesforce',
            crmAction: 'create_lead'
          }
        }
      },
      {
        id: '5',
        type: 'action',
        position: { x: 400, y: 350 },
        data: {
          label: 'Nurturing Email',
          actionType: 'send_message',
          config: {
            message: 'Grazie per il tuo interesse! Ti invieremo maggiori informazioni via email.'
          }
        }
      }
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3', animated: true },
      { id: 'e3-4', source: '3', target: '4', animated: true, sourceHandle: 'true' },
      { id: 'e3-5', source: '3', target: '5', animated: true, sourceHandle: 'false' }
    ]
  },

  // Appointment Booking
  {
    id: 'appointment-booking',
    name: 'Prenotazione Appuntamenti',
    description: 'Sistema automatico per prenotazione e reminder appuntamenti',
    category: 'Servizi',
    icon: '📅',
    tags: ['appuntamento', 'prenotazione', 'reminder'],
    nodes: [
      {
        id: '1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Richiesta Appuntamento',
          triggerType: 'keyword',
          config: {
            keyword: 'appuntamento'
          }
        }
      },
      {
        id: '2',
        type: 'action',
        position: { x: 250, y: 150 },
        data: {
          label: 'Mostra Calendario',
          actionType: 'send_list',
          config: {
            listSections: [
              {
                title: 'Orari Disponibili',
                rows: []
              }
            ]
          }
        }
      },
      {
        id: '3',
        type: 'integration',
        position: { x: 250, y: 250 },
        data: {
          label: 'Crea Evento',
          integrationType: 'calendar',
          config: {
            calendarEvent: {
              title: 'Appuntamento Cliente',
              duration: 30
            }
          }
        }
      },
      {
        id: '4',
        type: 'template',
        position: { x: 250, y: 350 },
        data: {
          label: 'Conferma Appuntamento',
          templateType: 'appointment',
          config: {
            language: 'it'
          }
        }
      },
      {
        id: '5',
        type: 'delay',
        position: { x: 250, y: 450 },
        data: {
          label: 'Reminder -1 giorno',
          delayType: 'custom_schedule',
          config: {
            customSchedule: ['-1 day @ 10:00']
          }
        }
      },
      {
        id: '6',
        type: 'action',
        position: { x: 250, y: 550 },
        data: {
          label: 'Invia Reminder',
          actionType: 'send_message',
          config: {
            message: '⏰ Promemoria: Hai un appuntamento domani alle {{appointment_time}}'
          }
        }
      }
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3', animated: true },
      { id: 'e3-4', source: '3', target: '4', animated: true },
      { id: 'e4-5', source: '4', target: '5', animated: true },
      { id: 'e5-6', source: '5', target: '6', animated: true }
    ]
  },

  // Feedback Collection
  {
    id: 'feedback-survey',
    name: 'Raccolta Feedback',
    description: 'Sondaggio post-acquisto con analisi sentiment',
    category: 'Feedback',
    icon: '📊',
    tags: ['feedback', 'sondaggio', 'sentiment'],
    nodes: [
      {
        id: '1',
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: {
          label: 'Post Acquisto +3gg',
          triggerType: 'webhook',
          config: {
            webhookUrl: '/api/post-purchase'
          }
        }
      },
      {
        id: '2',
        type: 'delay',
        position: { x: 250, y: 150 },
        data: {
          label: 'Attendi 3 giorni',
          delayType: 'fixed',
          config: {
            delayTime: 3,
            delayUnit: 'days'
          }
        }
      },
      {
        id: '3',
        type: 'template',
        position: { x: 250, y: 250 },
        data: {
          label: 'Template Feedback',
          templateType: 'feedback',
          config: {
            language: 'it'
          }
        }
      },
      {
        id: '4',
        type: 'action',
        position: { x: 250, y: 350 },
        data: {
          label: 'Crea Sondaggio',
          actionType: 'create_poll',
          config: {
            pollQuestion: 'Come valuteresti la tua esperienza?',
            pollOptions: ['⭐⭐⭐⭐⭐', '⭐⭐⭐⭐', '⭐⭐⭐', '⭐⭐', '⭐']
          }
        }
      },
      {
        id: '5',
        type: 'condition',
        position: { x: 250, y: 450 },
        data: {
          label: 'Analisi Sentiment',
          conditionType: 'sentiment_analysis',
          config: {
            sentiment: 'negative'
          }
        }
      },
      {
        id: '6',
        type: 'action',
        position: { x: 100, y: 550 },
        data: {
          label: 'Gestione Reclamo',
          actionType: 'send_message',
          config: {
            message: 'Ci dispiace per la tua esperienza. Un nostro responsabile ti contatterà presto.'
          }
        }
      },
      {
        id: '7',
        type: 'action',
        position: { x: 400, y: 550 },
        data: {
          label: 'Ringraziamento',
          actionType: 'send_message',
          config: {
            message: 'Grazie per il tuo feedback positivo! 🎉'
          }
        }
      }
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3', animated: true },
      { id: 'e3-4', source: '3', target: '4', animated: true },
      { id: 'e4-5', source: '4', target: '5', animated: true },
      { id: 'e5-6', source: '5', target: '6', animated: true, sourceHandle: 'true' },
      { id: 'e5-7', source: '5', target: '7', animated: true, sourceHandle: 'false' }
    ]
  }
];

export const templateCategories = [
  { id: 'all', name: 'Tutti', icon: '🌟' },
  { id: 'Caffè Sempione', name: 'Caffè Sempione', icon: '☕' },
  { id: 'Supporto Cliente', name: 'Supporto Cliente', icon: '🎧' },
  { id: 'E-commerce', name: 'E-commerce', icon: '🛒' },
  { id: 'Marketing', name: 'Marketing', icon: '📨' },
  { id: 'Lead Generation', name: 'Lead Generation', icon: '🎯' },
  { id: 'Servizi', name: 'Servizi', icon: '📅' },
  { id: 'Feedback', name: 'Feedback', icon: '📊' }
];
